import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import {
  PlayerFieldStatus,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Bench } from '@sorare/core/src/atoms/icons/Bench';
import { iconCheckCircle } from '@sorare/core/src/atoms/icons/system/iconCheckCircle.ts';
import { iconXCircle } from '@sorare/core/src/atoms/icons/system/iconXCircle.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { isType } from '@sorare/core/src/lib/gql';
import { shortStatusMessages } from '@sorare/core/src/lib/players';

import {
  PlayerGameStatsPrediction,
  ShapeContainer,
} from '#src/components/game/PlayerGameStatsPrediction/index.tsx';
import {
  isAnnouncedStarter,
  isAnnouncedSubstitute,
} from '#src/lib/playingStatus.ts';
import { isGameScheduled } from '#src/shared/game/lib.tsx';

import type { ScheduledPlayerGameScoreState_playerGameScore } from './__generated__/index.graphql.ts';

type Props = {
  variant: 'chip' | 'cornerBadge' | 'text' | 'column-text';
  includesIcon?: boolean;
  playerGameScores: ScheduledPlayerGameScoreState_playerGameScore[];
  hidePredictions?: boolean;
};

const Wrapper = styled.span`
  display: inline-block;
`;

export const ScheduledPlayerGameScoreState = ({
  playerGameScores,
  variant,
  includesIcon = true,
  hidePredictions,
}: Props) => {
  const scheduledPlayerGameScores = playerGameScores
    .filter(({ anyGame }) => isGameScheduled(anyGame.status))
    .filter(pgs => isType(pgs.anyPlayerGameStats, 'PlayerGameStats'));

  const scheduledPlayerGameScore = scheduledPlayerGameScores[0];

  if (
    !scheduledPlayerGameScore ||
    !isType(scheduledPlayerGameScore.anyPlayerGameStats, 'PlayerGameStats')
  )
    return null;

  if (isAnnouncedStarter(scheduledPlayerGameScore)) {
    return (
      <Wrapper
        className={classNames({
          'max-score-glow': variant === 'chip' || variant === 'cornerBadge',
        })}
      >
        <ShapeContainer
          variant={variant}
          includesIcon={includesIcon}
          color="var(--c-white)"
        >
          <Horizontal as="span" gap={0.5}>
            <FontAwesomeIcon icon={iconCheckCircle} />
            <FormattedNumber value={1} style="percent" />
          </Horizontal>
        </ShapeContainer>
      </Wrapper>
    );
  }

  if (isAnnouncedSubstitute(scheduledPlayerGameScore)) {
    return (
      <Wrapper>
        <ShapeContainer
          Icon={Bench}
          variant={variant}
          includesIcon={includesIcon}
          color="var(--c-yellow-600)"
        >
          <FormattedNumber value={0} style="percent" />
        </ShapeContainer>
      </Wrapper>
    );
  }

  if (
    scheduledPlayerGameScore.anyPlayerGameStats?.fieldStatus ===
    PlayerFieldStatus.NOT_ON_GAME_SHEET
  ) {
    return (
      <Wrapper>
        <ShapeContainer
          variant={variant}
          includesIcon={includesIcon}
          color="var(--c-onBase-error)"
        >
          <Horizontal gap={0.5} as="span">
            <FontAwesomeIcon icon={iconXCircle} />
            <FormattedMessage
              {...shortStatusMessages[ScoreStatus.DID_NOT_PLAY]}
            />
          </Horizontal>
        </ShapeContainer>
      </Wrapper>
    );
  }
  if (
    isType(scheduledPlayerGameScore.anyPlayerGameStats, 'PlayerGameStats') &&
    scheduledPlayerGameScore.anyPlayerGameStats?.footballPlayingStatusOdds &&
    !hidePredictions
  ) {
    return (
      <PlayerGameStatsPrediction
        playingStatusOdds={
          scheduledPlayerGameScore.anyPlayerGameStats?.footballPlayingStatusOdds
        }
        variant={variant}
        includesIcon={includesIcon}
      />
    );
  }

  return null;
};

ScheduledPlayerGameScoreState.fragments = {
  playerGameScore: gql`
    fragment ScheduledPlayerGameScoreState_playerGameScore on PlayerGameScoreInterface {
      id
      anyGame {
        id
        status: statusTyped
      }
      anyPlayerGameStats {
        id
        ... on PlayerGameStats {
          id
          fieldStatus
          footballPlayingStatusOdds {
            ...PlayerGameStatsPrediction_playingStatusOdds
          }
        }
      }
      ...isAnnouncedStarter_playerGameScore
      ...isAnnouncedSubstitute_playerGameScore
    }
    ${PlayerGameStatsPrediction.fragments.playingStatusOdds}
    ${isAnnouncedStarter.fragments.playerGameScore}
    ${isAnnouncedSubstitute.fragments.playerGameScore}
  ` as TypedDocumentNode<ScheduledPlayerGameScoreState_playerGameScore>,
};
